/*
 * Application State should be all within this file.
 */

const app = {
  state: {
    // Current scenario index (0-based)
    currentScenarioIndex: 0,
    // Current round index within scenario (0-based)
    currentRoundIndex: 0,
    // Whether we're viewing an educational slide
    viewingEducationalSlide: false,
    // Whether we're viewing management options (scenario 2 only)
    viewingManagementOptions: false,
    // Track selected species IDs per round: { "scenario-1-round-1": ["smartweed", "millett"], ... }
    selectedSpecies: {},
    // Track which rounds are completed
    completedRounds: [],
    // Activity completion status
    activityComplete: false
  },
  mutations: {
    SET_CURRENT_SCENARIO(state, index) {
      state.currentScenarioIndex = index;
    },
    SET_CURRENT_ROUND(state, index) {
      state.currentRoundIndex = index;
    },
    SET_VIEWING_EDUCATIONAL_SLIDE(state, value) {
      state.viewingEducationalSlide = value;
    },
    SET_VIEWING_MANAGEMENT_OPTIONS(state, value) {
      state.viewingManagementOptions = value;
    },
    SELECT_SPECIES(state, { roundId, speciesId }) {
      if (!state.selectedSpecies[roundId]) {
        state.selectedSpecies[roundId] = [];
      }
      if (!state.selectedSpecies[roundId].includes(speciesId)) {
        state.selectedSpecies[roundId].push(speciesId);
      }
    },
    DESELECT_SPECIES(state, { roundId, speciesId }) {
      if (state.selectedSpecies[roundId]) {
        state.selectedSpecies[roundId] = state.selectedSpecies[roundId].filter(
          (id) => id !== speciesId
        );
      }
    },
    TOGGLE_SPECIES(state, { roundId, speciesId }) {
      if (!state.selectedSpecies[roundId]) {
        state.selectedSpecies[roundId] = [];
      }
      const index = state.selectedSpecies[roundId].indexOf(speciesId);
      if (index === -1) {
        state.selectedSpecies[roundId].push(speciesId);
      } else {
        state.selectedSpecies[roundId].splice(index, 1);
      }
    },
    COMPLETE_ROUND(state, roundId) {
      if (!state.completedRounds.includes(roundId)) {
        state.completedRounds.push(roundId);
      }
    },
    SET_ACTIVITY_COMPLETE(state, value) {
      state.activityComplete = value;
    },
    RESET_ACTIVITY(state) {
      state.currentScenarioIndex = 0;
      state.currentRoundIndex = 0;
      state.viewingEducationalSlide = false;
      state.viewingManagementOptions = false;
      state.selectedSpecies = {};
      state.completedRounds = [];
      state.activityComplete = false;
    },
    RESET_ROUND(state, roundId) {
      state.selectedSpecies[roundId] = [];
    }
  },
  actions: {
    setCurrentScenario({ commit }, index) {
      commit("SET_CURRENT_SCENARIO", index);
    },
    setCurrentRound({ commit }, index) {
      commit("SET_CURRENT_ROUND", index);
    },
    setViewingEducationalSlide({ commit }, value) {
      commit("SET_VIEWING_EDUCATIONAL_SLIDE", value);
    },
    setViewingManagementOptions({ commit }, value) {
      commit("SET_VIEWING_MANAGEMENT_OPTIONS", value);
    },
    selectSpecies({ commit }, payload) {
      commit("SELECT_SPECIES", payload);
    },
    deselectSpecies({ commit }, payload) {
      commit("DESELECT_SPECIES", payload);
    },
    toggleSpecies({ commit }, payload) {
      commit("TOGGLE_SPECIES", payload);
    },
    completeRound({ commit }, roundId) {
      commit("COMPLETE_ROUND", roundId);
    },
    setActivityComplete({ commit }, value) {
      commit("SET_ACTIVITY_COMPLETE", value);
    },
    resetActivity({ commit }) {
      commit("RESET_ACTIVITY");
    },
    resetRound({ commit }, roundId) {
      commit("RESET_ROUND", roundId);
    },
    nextRound(
      { commit, state },
      { totalRoundsInScenario, totalScenarios, hasManagementOptions }
    ) {
      const nextRoundIndex = state.currentRoundIndex + 1;

      // Check if there are more rounds in the current scenario
      if (nextRoundIndex < totalRoundsInScenario) {
        commit("SET_CURRENT_ROUND", nextRoundIndex);
      } else {
        // Round complete, show educational slide
        commit("SET_VIEWING_EDUCATIONAL_SLIDE", true);
      }
    },
    finishEducationalSlide(
      { commit, state },
      { totalScenarios, hasManagementOptions }
    ) {
      commit("SET_VIEWING_EDUCATIONAL_SLIDE", false);

      // Check if we need to show management options (between scenario 2 round 1 and round 2)
      if (
        hasManagementOptions &&
        state.currentScenarioIndex === 1 &&
        state.currentRoundIndex === 0
      ) {
        commit("SET_VIEWING_MANAGEMENT_OPTIONS", true);
        return;
      }

      // Move to next scenario or complete
      const nextScenarioIndex = state.currentScenarioIndex + 1;
      if (nextScenarioIndex < totalScenarios) {
        commit("SET_CURRENT_SCENARIO", nextScenarioIndex);
        commit("SET_CURRENT_ROUND", 0);
      } else {
        commit("SET_ACTIVITY_COMPLETE", true);
      }
    },
    finishManagementOptions({ commit }) {
      commit("SET_VIEWING_MANAGEMENT_OPTIONS", false);
      // Move to round 2 of scenario 2
      commit("SET_CURRENT_ROUND", 1);
    },
    mapFromDatastore({ commit, state }, payload) {
      let _payload = "";
      try {
        _payload = JSON.parse(payload);
      } catch (e) {
        _payload = payload;
      }

      if (_payload?.currentScenarioIndex !== undefined) {
        commit("SET_CURRENT_SCENARIO", _payload.currentScenarioIndex);
      }
      if (_payload?.currentRoundIndex !== undefined) {
        commit("SET_CURRENT_ROUND", _payload.currentRoundIndex);
      }
      if (_payload?.viewingEducationalSlide !== undefined) {
        commit(
          "SET_VIEWING_EDUCATIONAL_SLIDE",
          _payload.viewingEducationalSlide
        );
      }
      if (_payload?.viewingManagementOptions !== undefined) {
        commit(
          "SET_VIEWING_MANAGEMENT_OPTIONS",
          _payload.viewingManagementOptions
        );
      }
      if (_payload?.selectedSpecies) {
        Object.keys(_payload.selectedSpecies).forEach((roundId) => {
          _payload.selectedSpecies[roundId].forEach((speciesId) => {
            commit("SELECT_SPECIES", { roundId, speciesId });
          });
        });
      }
      if (_payload?.completedRounds) {
        _payload.completedRounds.forEach((roundId) => {
          commit("COMPLETE_ROUND", roundId);
        });
      }
      if (_payload?.activityComplete) {
        commit("SET_ACTIVITY_COMPLETE", _payload.activityComplete);
      }
    }
  },
  getters: {
    currentScenarioIndex: (state) => state.currentScenarioIndex,
    currentRoundIndex: (state) => state.currentRoundIndex,
    viewingEducationalSlide: (state) => state.viewingEducationalSlide,
    viewingManagementOptions: (state) => state.viewingManagementOptions,
    selectedSpecies: (state) => state.selectedSpecies,
    completedRounds: (state) => state.completedRounds,
    activityComplete: (state) => state.activityComplete,
    getSelectedForRound: (state) => (roundId) => {
      return state.selectedSpecies[roundId] || [];
    },
    isRoundComplete: (state) => (roundId) => {
      return state.completedRounds.includes(roundId);
    },
    mapToDatastore(state) {
      let payload = {
        currentScenarioIndex: state.currentScenarioIndex,
        currentRoundIndex: state.currentRoundIndex,
        viewingEducationalSlide: state.viewingEducationalSlide,
        viewingManagementOptions: state.viewingManagementOptions,
        selectedSpecies: state.selectedSpecies,
        completedRounds: state.completedRounds,
        activityComplete: state.activityComplete
      };

      payload = JSON.stringify(payload);
      return payload;
    }
  }
};

export default app;
